import { debounce, keyBy, chunk } from 'lodash';
import { autorun } from 'mobx';

import AnalyticsEventNames from './AnalyticsEventNames';
import ProductClass from '../types/ProductClass';
import OrderTotalClass from '../types/OrderTotalClass';

const IMPRESSION_MAX_BATCH_SIZE = 35;

const dimension1 = {
  VISITOR: 'Vierailija asiakas',
  LOGGED_IN_USER: 'Kirjautunut asiakas'
};

const dimension2 = {
  RETAILER: 'Tukkuasiakas',
  CONSUMER: 'Kuluttaja-asiakas'
};

export default class GoogleAnalyticsGTM {
  handleEvents = (event) => {
    switch (event.detail.name) {
      case AnalyticsEventNames.addToCart:
        this.addToCart(event.detail.payload);
        break;
      case AnalyticsEventNames.productClick:
        this.productClick(event.detail.payload);
        break;
      case AnalyticsEventNames.productImpressions:
        this.productImpressions(event.detail.payload);
        break;
      case AnalyticsEventNames.productDetail:
        this.productDetail(event.detail.payload);
        break;
      case AnalyticsEventNames.promotionClick:
        this.promotionClick(event.detail.payload);
        break;
      case AnalyticsEventNames.promoView:
        this.promoView(event.detail.payload);
        break;
      case AnalyticsEventNames.purchase:
        this.purchase(event.detail.payload);
        break;
      case AnalyticsEventNames.removeFromCart:
        this.removeFromCart(event.detail.payload);
        break;
      case AnalyticsEventNames.reserveAndCollect:
        this.sendReserveAndCollect(event.detail.payload);
        break;
      case AnalyticsEventNames.updateDimension:
        this.updateDimension(event.detail.payload);
        break;
      case AnalyticsEventNames.checkout:
        this.checkout(event.detail.payload);
        break;
      case AnalyticsEventNames.pageNotFound:
        this.sendPageNotFoundError(event.detail.payload);
        break;
      default:
    }
  };

  constructor(accountStore) {
    this.accountStore = accountStore;

    this.initializeDataLayer();

    autorun(() => {
      const initialDataLayer = {
        loggedInStatus: this.accountStore.loggedIn
          ? dimension1.LOGGED_IN_USER
          : dimension1.VISITOR,
        customerType: this.accountStore.isRetailer
          ? dimension2.RETAILER
          : dimension2.CONSUMER,
        customerGroup: this.accountStore.account.group_name
      };

      window.dataLayer.push(initialDataLayer);
    });

    document.addEventListener('analytics', this.handleEvents, true);

    // We use our own impressionDataQueue & promoViewDataQueue to hold the data in order to send it in batches
    this.impressionDataQueue = [];
    this.promoViewDataQueue = [];

    // We don't want to spam Google Analytics, so we will always wait for 100ms for new data before sending impressions
    // and promoViews data out. If we have a constant flow of data, we will send data every 500 ms (maxWait).
    const wait = 100; // ms
    const maxWait = 500; // ms
    this.requestImpressionPushToDataLayer = debounce(
      this.pushImpressionsToDataLayer,
      wait,
      {
        maxWait
      }
    );
    this.requestPromoViewPushToDataLayer = debounce(
      this.pushPromoViewsToDataLayer,
      wait,
      {
        maxWait
      }
    );
  }

  initializeDataLayer() {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
  }

  /**
   * @param {string} currencyCode
   * @param {Array} productList An array with objects in the format { quantity, product, price, activeProductId }.
   */
  addToCart({ currencyCode, productList }) {
    const data = {
      ecommerce: {
        currencyCode,
        add: {
          products: productList.map((item) =>
            this.productListObjectToProductFieldObject(item)
          )
        }
      }
    };
    this.pushEventToDataLayer(data, AnalyticsEventNames.addToCart);
  }

  createCartProductFieldObject = (product) => {
    const id = product.product_id;
    const category = product.category_name;
    const price = product.total_price;
    const quantity = product.quantity;
    const name = product.name;

    const cartProductFieldObject = {
      id,
      quantity,
      name
    };

    if (category) {
      cartProductFieldObject.category = category;
    }

    if (price) {
      cartProductFieldObject.price = price;
    }

    return cartProductFieldObject;
  };

  cartDataToProductFieldObjectList = (product) => {
    const productFieldObject = this.createCartProductFieldObject(product);

    if (product.variations && product.variations.length > 0) {
      productFieldObject.variant = product.variations.join(',');
    }

    return productFieldObject;
  };

  checkout({ currencyCode, productList, actionField }) {
    const data = {
      ecommerce: {
        currencyCode,
        checkout: {
          actionField,
          products: productList.map((product) =>
            this.cartDataToProductFieldObjectList(product)
          )
        }
      }
    };
    this.pushEventToDataLayer(data, AnalyticsEventNames.checkout);
  }

  sendPageNotFoundError({ location, referrer }) {
    const data = {
      ecommerce: {
        pageNotFound: {
          address: location,
          referrer
        }
      }
    };
    this.pushEventToDataLayer(data, AnalyticsEventNames.pageNotFound);
  }

  /**
   * @param {string} currencyCode
   * @param {Array} productList An array with objects in the format { quantity, product, price, activeProductId }.
   */
  removeFromCart({ currencyCode, productList }) {
    const data = {
      ecommerce: {
        currencyCode,
        remove: {
          products: productList.map((item) =>
            this.productListObjectToProductFieldObject(item)
          )
        }
      }
    };
    this.pushEventToDataLayer(data, AnalyticsEventNames.removeFromCart);
  }

  /**
   * @param {string} currencyCode
   * @param {Array} productList An array with objects in the format { position, product }.
   * @param {string} listName
   */
  productClick({ currencyCode, productList, listName }) {
    const data = {
      ecommerce: {
        currencyCode,
        click: {
          products: productList.map((item) =>
            this.productListObjectToProductFieldObject(item)
          )
        }
      }
    };
    if (listName) {
      data.ecommerce.click.actionField = { list: listName };
    }
    this.pushEventToDataLayer(data, AnalyticsEventNames.productClick);
  }

  /**
   * @param {string} currencyCode
   * @param {Array} productList An array with objects in the format { position, product }.
   * @param {string} listName
   */
  productImpressions({ currencyCode, productList, listName }) {
    const impressions = productList.map((item) =>
      this.productListObjectToProductFieldObject(item)
    );
    if (listName) {
      impressions.forEach((productFieldObject) => {
        productFieldObject.list = listName;
      });
    }
    this.pushToImpressionDataQueue(impressions, currencyCode);
  }

  /**
   * @param {string} currencyCode
   * @param {Array} productList An array with objects in the format { product }.
   */
  productDetail({ currencyCode, productList }) {
    const data = {
      ecommerce: {
        currencyCode,
        detail: {
          products: productList.map((item) =>
            this.productListObjectToProductFieldObject(item)
          )
        }
      }
    };
    this.pushEventToDataLayer(data, AnalyticsEventNames.productDetail);
  }

  /**
   * @param {Array} bannerList An array with objects in the format { banner, bannerZone }.
   */
  promotionClick({ bannerList }) {
    const data = {
      ecommerce: {
        promoClick: {
          promotions: bannerList.map((item) =>
            this.bannerListobjectToPromoFieldObject(item)
          )
        }
      }
    };
    this.pushEventToDataLayer(data, AnalyticsEventNames.promotionClick);
  }

  /**
   * @param {Array} bannerList An array with objects in the format { banner, bannerZone }.
   */
  promoView({ bannerList }) {
    const promotions = bannerList.map((item) =>
      this.bannerListobjectToPromoFieldObject(item)
    );
    this.pushToPromoViewDataQueue(promotions);
  }

  /**
   * @param {Order} order An order
   * @param {Product[]} products An array with products
   */
  purchase({ order, products }) {
    const productDataById = keyBy(products, 'id');
    const orderTotalsByClass = keyBy(order.totals, 'class');

    const getTotalByClass = (totalClass) => {
      const total = orderTotalsByClass[totalClass];

      // The totals we use should always be there, but...
      return total ? total.analytics_value : null;
    };

    const orderProducts = order.products
      .map((product) => {
        const matchingPhysicalProduct = productDataById[product.product_id];

        // This conveniently filters out the "meta products", e.g. shipping or
        // service fees, which we don't have in the Products API.
        // (We don't currently know the types of the included products.)
        if (!matchingPhysicalProduct) {
          return null;
        }

        const isWholeNumber = !!(
          product.package_size && product.package_size % 1 === 0
        );

        return this.productListObjectToProductFieldObject(
          {
            product: matchingPhysicalProduct,
            quantity: isWholeNumber
              ? product.package_size * product.quantity
              : product.quantity,
            price:
              !isWholeNumber && product.package_size !== null
                ? product.package_size * product.analytics_final_unit_price
                : product.analytics_final_unit_price,
            activeProductId: product.id
          },
          true
        );
      })
      .filter((product) => !!product);

    const data = {
      ecommerce: {
        currencyCode: order.analytics_currency,
        purchase: {
          actionField: {
            id: order.id_for_analytics,
            revenue: getTotalByClass(OrderTotalClass.TOTAL),
            tax: getTotalByClass(OrderTotalClass.TAX_TOTAL),
            shipping: getTotalByClass(OrderTotalClass.EXTRA_TOTAL),
            coupon: order.campaign_code
          },
          products: orderProducts
        }
      }
    };

    this.pushEventToDataLayer(data, AnalyticsEventNames.purchase);
  }

  sendReserveAndCollect(data) {
    const analyticsData = {
      ecommerce: {
        ...data
      }
    };

    this.pushEventToDataLayer(
      analyticsData,
      AnalyticsEventNames.reserveAndCollect
    );
  }

  bannerListobjectToPromoFieldObject({ bannerZone, banner }) {
    // TODO Check name & creative fields with marketing
    return {
      id: banner.id,
      name: banner.title || banner.image,
      creative: banner.image,
      position: bannerZone
    };
  }

  productListObjectToProductFieldObject(
    { product, quantity, position, price, newQuantity, activeProductId },
    knownProduct = false
  ) {
    if (product.class === ProductClass.MULTI && activeProductId) {
      product = product.multi.findChild(activeProductId) || product;
    }

    // Product is known when it is in checkout or later stage
    const productName = knownProduct
      ? product.name
      : product.multiproduct_title || product.name;

    const productFieldObject = {
      name: productName,
      id: product.id,
      category:
        product.mainCategory &&
        product.mainCategory.hierarchy
          .map((category) => category.name)
          .join('/')
    };

    if (product.class === ProductClass.COLLECTION && activeProductId) {
      productFieldObject.variant = this.formatCollectionVariant(
        product.collection,
        activeProductId
      );
    }

    // If no numeric price is given, default to price with tax
    if (!(Number(price) === price)) {
      price = product.price_info ? product.price_info.getPrice(true) : 0.0;
    }
    productFieldObject.price = price.toFixed(2);

    if (product.manufacturer) {
      productFieldObject.brand = product.manufacturer.name;
    }

    // Product may have newQuantity or quantity field.
    const realQuantity = newQuantity || quantity;

    if (realQuantity) {
      productFieldObject.quantity = realQuantity;
    }
    if (position) {
      productFieldObject.position = position;
    }
    return productFieldObject;
  }

  formatCollectionVariant(collection, activeProductId) {
    const item = collection.getItemWithProductId(activeProductId);
    const columnElement = collection.column.getElementWithId(item.column_id);
    const columnElementFormatted = this.formatPropertyElement(columnElement);
    const rowElement = collection.row.getElementWithId(item.row_id);
    const rowElementFormatted = this.formatPropertyElement(rowElement);
    return `${columnElementFormatted} - ${rowElementFormatted}`;
  }

  formatPropertyElement(element) {
    return `${element.name} (ID: ${element.id})`;
  }

  /**
   * Impressions come from multiple components, so we queue them instead of sending them right away.
   *
   * @param impressions
   * @param currencyCode
   */
  pushToImpressionDataQueue(impressions, currencyCode) {
    this.impressionCurrencyCode = currencyCode;
    this.impressionDataQueue = this.impressionDataQueue.concat(impressions);
    this.requestImpressionPushToDataLayer();
  }

  pushImpressionsToDataLayer() {
    // GA has a maximum payload size of 8KB. If we have too many impressions we need to split them into chunks.
    const impressionChunks = chunk(
      this.impressionDataQueue,
      IMPRESSION_MAX_BATCH_SIZE
    );
    impressionChunks.forEach((impressionChunk) =>
      this.pushEventToDataLayer(
        {
          ecommerce: {
            currencyCode: this.impressionCurrencyCode,
            impressions: impressionChunk
          }
        },
        AnalyticsEventNames.productImpressions
      )
    );
    this.impressionDataQueue = [];
  }

  /**
   * Promo views come from multiple components, so we queue them instead of sending them right away.
   * @param promoViews
   */
  pushToPromoViewDataQueue(promoViews) {
    this.promoViewDataQueue = this.promoViewDataQueue.concat(promoViews);
    this.requestPromoViewPushToDataLayer();
  }

  pushPromoViewsToDataLayer() {
    // GA has a maximum payload size of 8KB. If we have too many impressions we need to split them into chunks.
    const chunks = chunk(this.promoViewDataQueue, IMPRESSION_MAX_BATCH_SIZE);
    chunks.forEach((chunk) =>
      this.pushEventToDataLayer(
        {
          ecommerce: {
            promoView: {
              promotions: chunk
            }
          }
        },
        AnalyticsEventNames.promoView
      )
    );
    this.impressionDataQueue = [];
  }

  updateDimension({ dimensionKey, dimensionValue }) {
    window.dataLayer[0][dimensionKey] = dimensionValue;
  }

  pushEventToDataLayer(data, action) {
    window.dataLayer.push({
      // Send all data under a generic "ecommerce" -event. -- But why?
      // Event name here should be what we use in the eventAction, https://developers.google.com/tag-manager/enhanced-ecommerce
      // Potentially disrupts the analytics of many clients, if name/structure is changed without proper planning.
      // It seems to work but creates confusion why it's different of the documentation.
      event: 'ecommerce',
      eventAction: action,
      ...data
    });

    // Reset the ecommerce data after pushes. -- But why?
    window.dataLayer.push({ eventAction: undefined, ecommerce: undefined });
  }
}



// WEBPACK FOOTER //
// ./src/analytics/GoogleAnalyticsGTM.js